import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ActivityIndicator from '../../../../components/ActivityIndicator';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import WhatsAppCampaignModal from '../../../whatsappCampaign/components/WhatsAppCampaignModal';
import { WhatsAppCampaignSelection } from '../../../whatsappCampaign/types';
import CustomerIntelligenceChart from '../CustomerIntelligenceChart';
import {
  getCustomerIntelligenceChart,
  exportCustomerIntelligenceReport,
  getCustomerIntelligenceReport,
  toDateOnly,
} from '../../services/customerIntelligenceService';
import readBlobAsText from '../../../../utils/readBlobAsText';
import {
  AbcCurveRow,
  AtRiskRow,
  AverageTicketRow,
  BestCustomerRow,
  CustomerIntelligenceFilters,
  CustomerIntelligenceSort,
  CustomerIntelligenceSortColumn,
  CustomerIntelligenceSortDirection,
  CustomerIntelligenceSortKey,
  CUSTOMER_INTELLIGENCE_SORT_COLUMNS,
  CustomerIntelligenceChartResponse,
  CustomerIntelligenceResponse,
  CustomerReportType,
  PurchaseFrequencyRow,
  SalesByNeighborhoodRow,
} from '../../types';
import {
  ApplyButton,
  Badge,
  CheckboxCell,
  CheckboxHeaderCell,
  Field,
  FilterActions,
  ExportButton,
  Filters,
  FilterGroup,
  Pagination,
  RetryButton,
  Section,
  SectionHeader,
  SortButton,
  SortIndicator,
  Status,
  Tab,
  Table,
  TableContainer,
  Tabs,
  Toolbar,
  WhatsAppButton,
} from './styles';

interface CustomerIntelligenceSectionProps {
  companyName?: string;
}

interface ReportTab {
  type: CustomerReportType;
  label: string;
}

const REPORT_TABS: ReportTab[] = [
  { type: 'best-customers', label: 'Melhores Clientes' },
  { type: 'purchase-frequency', label: 'Frequência de Compras' },
  { type: 'average-ticket', label: 'Ticket Médio' },
  { type: 'abc-curve', label: 'Curva ABC' },
  { type: 'at-risk', label: 'Clientes em Risco' },
  { type: 'sales-by-neighborhood', label: 'Vendas por Bairro' },
];

const PAGE_SIZE = 50;
const DEFAULT_REPORT_PERIOD_DAYS = 90;
const MAX_REPORT_PERIOD_DAYS = 366;
const EXPORT_ROW_LIMIT = 10000;

type SelectionMode = WhatsAppCampaignSelection['mode'];
type HeaderCheckboxState = 'checked' | 'unchecked' | 'indeterminate';

interface TableSelectionProps {
  isSelected: (customerId: number) => boolean;
  onToggleRow: (customerId: number) => void;
  headerState: HeaderCheckboxState;
  onToggleAll: () => void;
}

interface SortableHeaderProps {
  column: CustomerIntelligenceSortColumn;
  sort: CustomerIntelligenceSort | null;
  onSort: (sortBy: CustomerIntelligenceSortKey) => void;
}

function subtractDays(value: Date, days: number): Date {
  const result = new Date(value.getTime());
  result.setDate(result.getDate() - days);
  return result;
}

function initialFilters(now = new Date(Date.now())): CustomerIntelligenceFilters {
  return {
    startDate: toDateOnly(subtractDays(now, DEFAULT_REPORT_PERIOD_DAYS - 1)),
    endDate: toDateOnly(now),
  };
}

function appliedFiltersKey(
  reportType: CustomerReportType,
  filters: CustomerIntelligenceFilters,
): string {
  return JSON.stringify({
    reportType,
    startDate: filters.startDate,
    endDate: filters.endDate,
    ...(reportType === 'abc-curve' || reportType === 'at-risk'
      ? { curve: filters.curve || null }
      : {}),
    ...(reportType === 'at-risk'
      ? {
          risk: filters.risk || null,
          daysWithoutPurchase: filters.daysWithoutPurchase === undefined
            ? null
            : filters.daysWithoutPurchase,
        }
      : {}),
  });
}

function parseDateOnly(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return toDateOnly(parsed) === value ? parsed : null;
}

function getPeriodValidationError(filters: CustomerIntelligenceFilters): string | null {
  const startDate = parseDateOnly(filters.startDate);
  const endDate = parseDateOnly(filters.endDate);

  if (!startDate || !endDate) {
    return 'Informe datas válidas para o período da análise.';
  }

  if (startDate > endDate) {
    return 'A data inicial não pode ser posterior à data final.';
  }

  const days = Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
  if (days > MAX_REPORT_PERIOD_DAYS) {
    return 'O período da análise pode ter no máximo 366 dias.';
  }

  return null;
}

function currency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function number(value: number | null, digits = 1): string {
  if (value === null) return '—';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function date(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
}

function HeaderSelectionCheckbox({
  state,
  onToggle,
}: {
  state: HeaderCheckboxState;
  onToggle: () => void;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = state === 'indeterminate';
    }
  }, [state]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'checked'}
      onChange={onToggle}
      aria-label="Selecionar todos"
    />
  );
}

function RowSelectionCheckbox({
  customerId,
  customerName,
  checked,
  onToggle,
}: {
  customerId: number;
  customerName: string;
  checked: boolean;
  onToggle: (customerId: number) => void;
}): JSX.Element {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={() => onToggle(customerId)}
      aria-label={`Selecionar ${customerName}`}
    />
  );
}

function SelectionHeader({ selection }: { selection: TableSelectionProps }): JSX.Element {
  return (
    <CheckboxHeaderCell>
      <HeaderSelectionCheckbox state={selection.headerState} onToggle={selection.onToggleAll} />
    </CheckboxHeaderCell>
  );
}

function SelectionCell({
  customerId,
  customerName,
  selection,
}: {
  customerId: number;
  customerName: string;
  selection: TableSelectionProps;
}): JSX.Element {
  return (
    <CheckboxCell>
      <RowSelectionCheckbox
        customerId={customerId}
        customerName={customerName}
        checked={selection.isSelected(customerId)}
        onToggle={selection.onToggleRow}
      />
    </CheckboxCell>
  );
}

function paginationSummary(
  pagination: CustomerIntelligenceResponse['pagination'],
): string {
  if (pagination.total === 0) {
    return `Exibindo 0–0 de 0 resultados · ${pagination.pageSize} por página`;
  }

  const first = (pagination.page - 1) * pagination.pageSize + 1;
  const last = Math.min(pagination.page * pagination.pageSize, pagination.total);
  return `Exibindo ${first}–${last} de ${pagination.total} resultados · ${pagination.pageSize} por página`;
}

function directionLabel(direction: CustomerIntelligenceSortDirection): string {
  return direction === 'asc' ? 'crescente' : 'decrescente';
}

function ariaSortValue(
  direction?: CustomerIntelligenceSortDirection,
): 'none' | 'ascending' | 'descending' {
  if (!direction) return 'none';
  return direction === 'asc' ? 'ascending' : 'descending';
}

function SortableHeader({ column, sort, onSort }: SortableHeaderProps): JSX.Element {
  const active = sort?.sortBy === column.sortBy;
  const currentDirection = active && sort ? sort.sortDirection : undefined;
  const nextDirection: CustomerIntelligenceSortDirection =
    currentDirection === 'asc' ? 'desc' : 'asc';
  const accessibleName = currentDirection
    ? `${column.label}. Ordenação atual ${directionLabel(currentDirection)}. Clique para ordem ${directionLabel(nextDirection)}.`
    : `${column.label}. Clique para ordenar em ordem crescente.`;

  return (
    <th aria-sort={ariaSortValue(currentDirection)}>
      <SortButton type="button" aria-label={accessibleName} onClick={() => onSort(column.sortBy)}>
        {column.label}
        {currentDirection && (
          <SortIndicator aria-hidden="true">
            {currentDirection === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </SortIndicator>
        )}
      </SortButton>
    </th>
  );
}

function renderSortableHeaders(
  reportType: CustomerReportType,
  sort: CustomerIntelligenceSort | null,
  onSort: (sortBy: CustomerIntelligenceSortKey) => void,
): React.ReactNode {
  return CUSTOMER_INTELLIGENCE_SORT_COLUMNS[reportType].map(column => (
    <SortableHeader key={column.sortBy} column={column} sort={sort} onSort={onSort} />
  ));
}

function CustomerColumns({ row }: { row: BestCustomerRow }): JSX.Element {
  return (
    <>
      <td>{row.customer}</td>
      <td>{row.phone || '—'}</td>
      <td>{row.email || '—'}</td>
    </>
  );
}

function renderTable(
  reportType: CustomerReportType,
  response: CustomerIntelligenceResponse,
  selection?: TableSelectionProps,
  sort: CustomerIntelligenceSort | null = null,
  onSort: (sortBy: CustomerIntelligenceSortKey) => void = () => undefined,
): React.ReactNode {
  switch (reportType) {
    case 'best-customers':
      return (
        <TableContainer>
          <Table>
            <caption className="sr-only">Melhores Clientes</caption>
            <thead>
              <tr>
                {selection && <SelectionHeader selection={selection} />}
                {renderSortableHeaders('best-customers', sort, onSort)}
              </tr>
            </thead>
            <tbody>
              {(response.data as BestCustomerRow[]).map(row => (
                <tr key={row.customerId}>
                  {selection && (
                    <SelectionCell
                      customerId={row.customerId}
                      customerName={row.customer}
                      selection={selection}
                    />
                  )}
                  <CustomerColumns row={row} />
                  <td>{row.orderCount}</td><td>{currency(row.totalPurchased)}</td>
                  <td>{currency(row.averageTicket)}</td><td>{date(row.firstPurchase)}</td>
                  <td>{date(row.lastPurchase)}</td><td>{row.daysWithoutPurchase}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      );
    case 'purchase-frequency':
      return (
        <TableContainer><Table><caption className="sr-only">Frequência de Compras</caption>
          <thead><tr>
            {selection && <SelectionHeader selection={selection} />}
            {renderSortableHeaders('purchase-frequency', sort, onSort)}
          </tr></thead>
          <tbody>{(response.data as PurchaseFrequencyRow[]).map(row => <tr key={row.customerId}>
            {selection && (
              <SelectionCell
                customerId={row.customerId}
                customerName={row.customer}
                selection={selection}
              />
            )}
            <td>{row.customer}</td><td>{row.phone || '—'}</td><td>{row.email || '—'}</td><td>{row.orderCount}</td>
            <td>{number(row.averageIntervalDays)} dias</td><td>{row.frequencyProfile}</td><td>{date(row.firstPurchase)}</td><td>{date(row.lastPurchase)}</td><td>{row.daysWithoutPurchase}</td>
          </tr>)}</tbody>
        </Table></TableContainer>
      );
    case 'average-ticket':
      return (
        <TableContainer><Table><caption className="sr-only">Ticket Médio por Cliente</caption>
          <thead><tr>
            {selection && <SelectionHeader selection={selection} />}
            {renderSortableHeaders('average-ticket', sort, onSort)}
          </tr></thead>
          <tbody>{(response.data as AverageTicketRow[]).map(row => <tr key={row.customerId}>
            {selection && (
              <SelectionCell
                customerId={row.customerId}
                customerName={row.customer}
                selection={selection}
              />
            )}
            <td>{row.customer}</td><td>{row.phone || '—'}</td><td>{row.email || '—'}</td><td>{row.orderCount}</td><td>{currency(row.totalProducts)}</td><td>{currency(row.totalDiscounts)}</td><td>{currency(row.totalDelivery)}</td><td>{currency(row.totalPurchased)}</td><td>{currency(row.averageTicket)}</td>
          </tr>)}</tbody>
        </Table></TableContainer>
      );
    case 'abc-curve':
      return (
        <TableContainer><Table><caption className="sr-only">Curva ABC de Clientes</caption>
          <thead><tr>
            {selection && <SelectionHeader selection={selection} />}
            {renderSortableHeaders('abc-curve', sort, onSort)}
          </tr></thead>
          <tbody>{(response.data as AbcCurveRow[]).map(row => <tr key={row.customerId}>
            {selection && (
              <SelectionCell
                customerId={row.customerId}
                customerName={row.customer}
                selection={selection}
              />
            )}
            <td>{row.customer}</td><td><Badge tone="curve">{row.curve}</Badge></td><td>{number(row.participationPercentage)}%</td><td>{number(row.cumulativePercentage)}%</td><td>{row.orderCount}</td><td>{currency(row.totalPurchased)}</td><td>{currency(row.averageTicket)}</td><td>{date(row.lastPurchase)}</td>
          </tr>)}</tbody>
        </Table></TableContainer>
      );
    case 'at-risk':
      return (
        <TableContainer><Table><caption className="sr-only">Clientes em Risco</caption>
          <thead><tr>
            {selection && <SelectionHeader selection={selection} />}
            {renderSortableHeaders('at-risk', sort, onSort)}
          </tr></thead>
          <tbody>{(response.data as AtRiskRow[]).map(row => <tr key={row.customerId}>
            {selection && (
              <SelectionCell
                customerId={row.customerId}
                customerName={row.customer}
                selection={selection}
              />
            )}
            <td>{row.customer}</td><td><Badge tone="curve">{row.curve}</Badge></td><td><Badge tone="risk">{row.risk}</Badge></td><td>{row.orderCount}</td><td>{currency(row.totalPurchased)}</td><td>{currency(row.averageTicket)}</td><td>{number(row.averageIntervalDays)} dias</td><td>{number(row.delayFactor, 2)}x</td><td>{row.daysWithoutPurchase}</td><td>{date(row.lastPurchase)}</td>
          </tr>)}</tbody>
        </Table></TableContainer>
      );
    case 'sales-by-neighborhood':
      return (
        <TableContainer><Table><caption className="sr-only">Vendas por Bairro</caption>
          <thead><tr>{renderSortableHeaders('sales-by-neighborhood', sort, onSort)}</tr></thead>
          <tbody>{(response.data as SalesByNeighborhoodRow[]).map(row => <tr key={row.neighborhood}>
            <td>{row.neighborhood}</td><td>{row.orderCount}</td><td>{row.customerCount}</td><td>{currency(row.totalSold)}</td><td>{currency(row.averageTicket)}</td><td>{number(row.ordersPerCustomer, 2)}</td><td>{number(row.orderPercentage)}%</td>
          </tr>)}</tbody>
        </Table></TableContainer>
      );
    default:
      return null;
  }
}

const CustomerIntelligenceSection: React.FC<CustomerIntelligenceSectionProps> = ({
  companyName,
}) => {
  const [activeReport, setActiveReport] = useState<CustomerReportType>('best-customers');
  const [draftFilters, setDraftFilters] = useState<CustomerIntelligenceFilters>(() => initialFilters());
  const [appliedFilters, setAppliedFilters] = useState<CustomerIntelligenceFilters | null>(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<CustomerIntelligenceSort | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [response, setResponse] = useState<CustomerIntelligenceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [chartResponse, setChartResponse] = useState<CustomerIntelligenceChartResponse | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(false);
  const [chartReloadKey, setChartReloadKey] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('all');
  const [exceptionCustomerIds, setExceptionCustomerIds] = useState<Set<number>>(
    () => new Set(),
  );
  const requestSequence = useRef(0);
  const chartRequestSequence = useRef(0);
  const exportInFlight = useRef(false);
  const chartCache = useRef<Record<string, CustomerIntelligenceChartResponse>>({});
  const canSendWhatsApp =
    appliedFilters != null &&
    response != null &&
    activeReport !== 'sales-by-neighborhood';
  const canExport =
    appliedFilters != null &&
    response != null &&
    !error &&
    !loading &&
    response.pagination.total > 0 &&
    response.pagination.total <= EXPORT_ROW_LIMIT &&
    appliedFiltersKey(activeReport, response.appliedFilters) ===
      appliedFiltersKey(activeReport, appliedFilters) &&
    response.appliedSort != null;
  const exportLimitNotice =
    !loading &&
    appliedFilters != null &&
    response != null &&
    !error &&
    appliedFiltersKey(activeReport, response.appliedFilters) ===
      appliedFiltersKey(activeReport, appliedFilters) &&
    response.pagination.total > EXPORT_ROW_LIMIT;
  const periodValidationError = useMemo(() => getPeriodValidationError(draftFilters), [draftFilters]);

  useEffect(() => {
    if (!appliedFilters) return undefined;

    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;

    setLoading(true);
    setError(false);
    const request = sort
      ? getCustomerIntelligenceReport(activeReport, appliedFilters, page, PAGE_SIZE, sort)
      : getCustomerIntelligenceReport(activeReport, appliedFilters, page, PAGE_SIZE);

    request
      .then(result => {
        if (requestSequence.current !== sequence) return;
        setResponse(result);
        setLoading(false);
      })
      .catch(() => {
        if (requestSequence.current !== sequence) return;
        setError(true);
        setLoading(false);
      });

    return undefined;
  }, [activeReport, appliedFilters, page, reloadKey, sort]);

  useEffect(() => {
    if (!appliedFilters) return undefined;

    const cacheKey = appliedFiltersKey(activeReport, appliedFilters);
    const cachedResponse = chartCache.current[cacheKey];
    const sequence = chartRequestSequence.current + 1;
    chartRequestSequence.current = sequence;

    setChartError(false);
    if (cachedResponse) {
      setChartResponse(cachedResponse);
      setChartLoading(false);
      return undefined;
    }

    setChartLoading(true);
    getCustomerIntelligenceChart(activeReport, appliedFilters)
      .then(result => {
        if (chartRequestSequence.current !== sequence) return;
        chartCache.current[cacheKey] = result;
        setChartResponse(result);
        setChartLoading(false);
      })
      .catch(() => {
        if (chartRequestSequence.current !== sequence) return;
        setChartError(true);
        setChartLoading(false);
      });

    return undefined;
  }, [activeReport, appliedFilters, chartReloadKey]);

  const resetSelection = useCallback(() => {
    setSelectionMode('all');
    setExceptionCustomerIds(new Set());
  }, []);

  const isCustomerSelected = useCallback(
    (customerId: number) =>
      selectionMode === 'all'
        ? !exceptionCustomerIds.has(customerId)
        : exceptionCustomerIds.has(customerId),
    [exceptionCustomerIds, selectionMode],
  );

  const toggleCustomer = useCallback((customerId: number) => {
    setExceptionCustomerIds(current => {
      const next = new Set(current);
      if (next.has(customerId)) {
        next.delete(customerId);
      } else {
        next.add(customerId);
      }
      return next;
    });
  }, []);

  const headerCheckboxState: HeaderCheckboxState = useMemo(() => {
    if (exceptionCustomerIds.size === 0) {
      return selectionMode === 'all' ? 'checked' : 'unchecked';
    }
    return 'indeterminate';
  }, [exceptionCustomerIds, selectionMode]);

  const toggleSelectAll = useCallback(() => {
    if (selectionMode === 'all' && exceptionCustomerIds.size === 0) {
      setSelectionMode('none');
      setExceptionCustomerIds(new Set());
      return;
    }
    setSelectionMode('all');
    setExceptionCustomerIds(new Set());
  }, [exceptionCustomerIds, selectionMode]);

  const tableSelection = useMemo<TableSelectionProps>(
    () => ({
      isSelected: isCustomerSelected,
      onToggleRow: toggleCustomer,
      headerState: headerCheckboxState,
      onToggleAll: toggleSelectAll,
    }),
    [headerCheckboxState, isCustomerSelected, toggleCustomer, toggleSelectAll],
  );

  const campaignSelection = useMemo<WhatsAppCampaignSelection>(
    () => ({
      mode: selectionMode,
      exceptionCustomerIds: Array.from(exceptionCustomerIds),
    }),
    [exceptionCustomerIds, selectionMode],
  );

  const applyFilters = useCallback(() => {
    if (periodValidationError) return;
    const nextFilters = { ...draftFilters };
    delete chartCache.current[appliedFiltersKey(activeReport, nextFilters)];
    setPage(1);
    setSort(null);
    setResponse(null);
    setChartResponse(null);
    setExportStatus(null);
    setAppliedFilters(nextFilters);
    resetSelection();
  }, [activeReport, draftFilters, periodValidationError, resetSelection]);

  const handleReportChange = useCallback((report: CustomerReportType) => {
    if (report === activeReport) return;
    setActiveReport(report);
    setPage(1);
    setSort(null);
    setResponse(null);
    resetSelection();
    setChartResponse(null);
    setChartError(false);
    setExportStatus(null);
    if (report === 'sales-by-neighborhood') {
      setCampaignOpen(false);
    }
  }, [activeReport, resetSelection]);

  const handleSort = useCallback((sortBy: CustomerIntelligenceSortKey) => {
    setPage(1);
    setSort(current => {
      const confirmedSort = current || (
        response?.appliedSort?.sortBy === sortBy ? response.appliedSort : null
      );
      return {
        sortBy,
        sortDirection: confirmedSort?.sortBy === sortBy && confirmedSort.sortDirection === 'asc'
          ? 'desc'
          : 'asc',
      };
    });
  }, [response]);

  const handleExport = useCallback(async () => {
    if (!canExport || !appliedFilters || exportLoading || exportInFlight.current) return;

    const reportType = activeReport;
    const filters = { ...appliedFilters };
    const confirmedSort = sort ? response?.appliedSort : undefined;
    if (sort && !confirmedSort) return;
    exportInFlight.current = true;
    setExportLoading(true);
    setExportStatus(null);
    try {
      const result = await exportCustomerIntelligenceReport(reportType, filters, confirmedSort);
      const disposition = result.headers && result.headers['content-disposition'];
      const encodedName = typeof disposition === 'string'
        ? /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1]
        : undefined;
      let fileName = `inteligencia-comercial-${reportType}-${filters.startDate}-a-${filters.endDate}.xlsx`;
      if (encodedName) {
        try {
          const decodedName = decodeURIComponent(encodedName.replace(/^"|"$/g, ''));
          if (/^[\wÀ-ÿ.-]+\.xlsx$/i.test(decodedName)) fileName = decodedName;
        } catch {
          fileName = `inteligencia-comercial-${reportType}-${filters.startDate}-a-${filters.endDate}.xlsx`;
        }
      }

      const blobUrl = window.URL.createObjectURL(result.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      const exportedRows = result.headers && result.headers['x-exported-rows'];
      setExportStatus(`Excel gerado com ${exportedRows || response?.pagination.total || 0} linhas.`);
    } catch (caught) {
      let message = 'Não foi possível gerar o Excel. Tente novamente.';
      const data = (caught as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await readBlobAsText(data)) as { message?: string };
          if (parsed.message) message = parsed.message;
        } catch {
          message = 'Não foi possível gerar o Excel. Tente novamente.';
        }
      } else if (data != null && typeof data === 'object') {
        const candidate = (data as { message?: unknown }).message;
        if (typeof candidate === 'string') message = candidate;
      }
      setExportStatus(message);
    } finally {
      exportInFlight.current = false;
      setExportLoading(false);
    }
  }, [activeReport, appliedFilters, canExport, exportLoading, response, sort]);

  const reportLabel = REPORT_TABS.find(tab => tab.type === activeReport)?.label;

  return (
    <Section aria-labelledby="customer-intelligence-title">
      <SectionHeader>
        <div>
          <h2 id="customer-intelligence-title">Análises de clientes</h2>
          <p>Empresa: {companyName || 'empresa autenticada'}</p>
        </div>
      </SectionHeader>

      <Toolbar>
          <Tabs aria-label="Análises de clientes">
            {REPORT_TABS.map(tab => (
              <Tab
                key={tab.type}
                type="button"
                aria-pressed={activeReport === tab.type}
                active={activeReport === tab.type}
                onClick={() => handleReportChange(tab.type)}
              >
                {tab.label}
              </Tab>
            ))}
          </Tabs>

          <Filters>
            <FilterGroup>
              <legend>Período da análise</legend>
              <Field>
                Período inicial
                <input
                  type="date"
                  value={draftFilters.startDate}
                  aria-invalid={Boolean(periodValidationError)}
                  aria-describedby={periodValidationError ? 'customer-intelligence-period-error' : undefined}
                  onChange={event => {
                    const value = event.target.value;
                    setDraftFilters(filters => ({ ...filters, startDate: value }));
                  }}
                />
              </Field>
              <Field>
                Período final
                <input
                  type="date"
                  value={draftFilters.endDate}
                  aria-invalid={Boolean(periodValidationError)}
                  aria-describedby={periodValidationError ? 'customer-intelligence-period-error' : undefined}
                  onChange={event => {
                    const value = event.target.value;
                    setDraftFilters(filters => ({ ...filters, endDate: value }));
                  }}
                />
              </Field>
            </FilterGroup>

            {(activeReport === 'abc-curve' || activeReport === 'at-risk') && (
              <Field>
                Curva ABC
                <select
                  value={draftFilters.curve || ''}
                  onChange={event => {
                    const value = event.target.value;
                    setDraftFilters(filters => ({ ...filters, curve: value ? value as CustomerIntelligenceFilters['curve'] : undefined }));
                  }}
                >
                  <option value="">Todas</option><option value="A">A</option><option value="B">B</option><option value="C">C</option>
                </select>
              </Field>
            )}

            {activeReport === 'at-risk' && (
              <>
                <Field>
                  Nível de risco
                  <select
                    value={draftFilters.risk || ''}
                    onChange={event => {
                      const value = event.target.value;
                      setDraftFilters(filters => ({ ...filters, risk: value ? value as CustomerIntelligenceFilters['risk'] : undefined }));
                    }}
                  >
                    <option value="">Crítico a médio</option><option value="CRÍTICO">Crítico</option><option value="ALTO">Alto</option><option value="MÉDIO">Médio</option><option value="BAIXO">Baixo</option>
                  </select>
                </Field>
                <Field>
                  Mínimo de dias sem comprar
                  <input
                    type="number"
                    min="0"
                    max="3660"
                    value={draftFilters.daysWithoutPurchase === undefined ? '' : draftFilters.daysWithoutPurchase}
                    onChange={event => {
                      const value = event.target.value;
                      setDraftFilters(filters => ({ ...filters, daysWithoutPurchase: value === '' ? undefined : Number(value) }));
                    }}
                  />
                </Field>
              </>
            )}
            <FilterActions>
              <ApplyButton type="button" onClick={applyFilters} disabled={Boolean(periodValidationError)}>Aplicar filtros</ApplyButton>
              <ExportButton type="button" onClick={handleExport} disabled={!canExport || exportLoading}>
                {exportLoading ? 'Gerando Excel...' : 'Baixar Excel'}
              </ExportButton>
              {canSendWhatsApp && (
                <WhatsAppButton type="button" onClick={() => setCampaignOpen(true)}>
                  Enviar WhatsApp
                </WhatsAppButton>
              )}
            </FilterActions>
          </Filters>

          {periodValidationError && <Status id="customer-intelligence-period-error" role="alert" error>{periodValidationError}</Status>}
          {exportStatus && <Status aria-live="polite" role="status">{exportStatus}</Status>}
          {exportLimitNotice && (
            <Status role="alert">
              Foram encontrados {response?.pagination.total.toLocaleString('pt-BR')} resultados. O limite para exportação é {EXPORT_ROW_LIMIT.toLocaleString('pt-BR')} linhas. Refine o período ou os filtros para continuar.
            </Status>
          )}

          {appliedFilters && (
            <CustomerIntelligenceChart
              reportType={activeReport}
              response={chartResponse}
              totalResults={response?.pagination.total}
              loading={chartLoading}
              error={chartError}
              onRetry={() => {
                delete chartCache.current[appliedFiltersKey(activeReport, appliedFilters)];
                setChartReloadKey(value => value + 1);
              }}
            />
          )}

          {loading && <Status aria-live="polite"><span>Carregando dados...</span><ActivityIndicator /></Status>}
          {!loading && error && <Status error role="alert">Não foi possível carregar {reportLabel}.
            <br /><RetryButton type="button" onClick={() => setReloadKey(value => value + 1)}>Tentar novamente</RetryButton>
          </Status>}
          {!loading && !error && !appliedFilters && (
            <Status aria-live="polite">Revise o período e clique em Aplicar filtros para carregar a análise.</Status>
          )}
          {!loading && !error && appliedFilters && response && response.data.length === 0 && (
            <Status aria-live="polite">Não há vendas concluídas no período selecionado. Tente ampliar o período da análise.</Status>
          )}
          {!loading && !error && response && response.data.length > 0 && (
            <>
              {renderTable(
                activeReport,
                response,
                activeReport === 'sales-by-neighborhood' ? undefined : tableSelection,
                response.appliedSort,
                handleSort,
              )}
              <Pagination>
                <span>{paginationSummary(response.pagination)}</span>
                <div>
                  <button type="button" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Anterior</button>
                  <button type="button" disabled={!response.pagination.hasNextPage} onClick={() => setPage(value => value + 1)}>Próxima</button>
                </div>
              </Pagination>
            </>
          )}
        </Toolbar>

      {appliedFilters && (
        <WhatsAppCampaignModal
          isOpen={campaignOpen}
          reportType={activeReport}
          filters={appliedFilters}
          selection={campaignSelection}
          onClose={() => setCampaignOpen(false)}
        />
      )}
    </Section>
  );
};

export default CustomerIntelligenceSection;
